import { useEffect, useState } from "react";
import { CreditCard } from "lucide-react";
import type { Translate } from "../i18n";
import { CheckoutApiError, getOrderStatus } from "../lib/api";
import { forgetTokenForOrder, recallTokenForOrder } from "../lib/session-token";
import { BRAND } from "../lib/brand";
import { apiReturnUrl } from "../lib/return-url";

export function Cancelled({
  t,
  orderId,
  navigate
}: {
  t: Translate;
  orderId: string;
  navigate: (path: string) => void;
}) {
  // Retry returns to the review screen for the same order if we still hold the
  // access token (stashed at pay time); otherwise it falls back to the marketing
  // site (the order-ready email link reopens the review screen).
  const [tokenState, setTokenState] = useState<{ orderId: string; token: string | null }>(() => ({
    orderId,
    token: recallTokenForOrder(orderId)
  }));
  const token = tokenState.orderId === orderId ? tokenState.token : recallTokenForOrder(orderId);
  const [checkRun, setCheckRun] = useState(0);
  const [result, setResult] = useState<{
    orderId: string;
    kind: "checking" | "pending" | "unconfirmed";
    returnUrl: string | null;
  }>({ orderId, kind: "checking", returnUrl: null });
  const current = result.orderId === orderId ? result : { kind: "checking", returnUrl: null };
  const orderReference = `TK-${orderId.replace(/-/g, "").slice(0, 8).toUpperCase()}`;

  useEffect(() => {
    if (!token) {
      setResult({ orderId, kind: "unconfirmed", returnUrl: null });
      return;
    }
    setResult({ orderId, kind: "checking", returnUrl: null });
    const controller = new AbortController();
    let obsolete = false;
    void getOrderStatus(token, controller.signal)
      .then((status) => {
        if (obsolete) return;
        if (status.orderId !== orderId) {
          forgetTokenForOrder(orderId);
          setTokenState({ orderId, token: null });
          setResult({ orderId, kind: "unconfirmed", returnUrl: null });
          return;
        }
        const paymentStatus = status.paymentStatus === undefined
          ? status.paid === true ? "paid" : status.paid === false ? "pending" : null
          : status.paymentStatus;
        // The cancel URL only says the browser left Stripe. A second tab, Back,
        // or an old bookmark can reach it after authorization, capture or refund.
        // Reuse the authenticated confirmation view for those durable outcomes.
        if (paymentStatus !== null && ["paid", "authorized", "cancelled"].includes(paymentStatus)) {
          navigate(`/orders/${orderId}/paid`);
          return;
        }
        setResult({ orderId, kind: paymentStatus === "pending" ? "pending" : "unconfirmed",
          returnUrl: apiReturnUrl(status.returnUrl) });
      })
      .catch((error: unknown) => {
        if (obsolete) return;
        setResult({ orderId, kind: "unconfirmed", returnUrl: null });
        if (error instanceof CheckoutApiError && (error.status === 404 || error.status === 410)) {
          forgetTokenForOrder(orderId);
          setTokenState({ orderId, token: null });
        }
      });
    return () => {
      obsolete = true;
      controller.abort();
    };
  }, [orderId, token, checkRun, navigate]);

  if (current.kind === "checking") return <main className="co-state">
    <div className="co-spinner" aria-hidden />
    <p>{t("success.confirming")}</p>
  </main>;

  return (
    <main className="co-center">
      <div className="co-check" aria-hidden style={{ background: "color-mix(in srgb, #d6622d 12%, transparent)", color: "#d6622d" }}>
        <CreditCard size={42} />
      </div>
      <h1>{t(current.kind === "pending" ? "cancelled.title" : "success.confirming")}</h1>
      {/* Even a pending local read can precede payment reconciliation. Never
          infer "nothing was charged" from a redirect or an unavailable API. */}
      <p>{t(current.kind === "pending" ? "cancelled.body" : "success.notConfirmed")}</p>

      <section className="co-card" style={{ width: "100%", maxWidth: 360 }}>
        <div className="co-price-row">
          <span>{t("cancelled.orderRef")}</span>
          <span>#{orderReference}</span>
        </div>
        {current.kind === "pending" && <p className="co-summary-row" style={{ marginTop: 8 }}>
          <span className="co-diamond is-small" aria-hidden />
          <span style={{ fontSize: 14 }}>{t("cancelled.tip")}</span>
        </p>}
      </section>

      <div className="co-stack">
        <button
          type="button"
          className="co-cta"
          onClick={() => {
            if (token && current.kind === "pending") navigate(`/o/${token}`);
            else if (token) setCheckRun((run) => run + 1);
            else window.location.assign(BRAND.marketingUrl);
          }}
        >
          {t(token ? current.kind === "pending" ? "cancelled.retry" : "success.checkAgain" : "success.back")}
        </button>
        {current.returnUrl && (
          <a className="co-btn-secondary" href={current.returnUrl}>
            {t("success.returnToSite")}
          </a>
        )}
        <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>
          {t("cancelled.help")}
        </a>
      </div>
    </main>
  );
}
